<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '600px'"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
	>
		<v-card class="sgnl-pb-0">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Your new secret SCCE API key
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body sgnl-paragraph-s-regular">
				<div>
					Use this key in your application to make programmatic requests to the Signaloid Cloud Compute
					Engine. Put the key as the value of the Authorization HTTP header.
				</div>
				<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
					<v-text-field
						:model-value="passedKey"
						:class="'sgnl-flex-grow-1 sgnl-font-weight-medium' + (keyVisible ? '' : ' obscured')"
						readonly
						hide-details
						data-test="api-key-creation-dialog-key-textfield"
					/>
					<TooltipButton
						@click="toggleKeyVisible"
						:tooltipText="keyVisible ? 'Hide key' : 'Reveal key'"
						:icon="true"
					>
						<template #btnContent>
							<ViewOff16 v-if="keyVisible" />
							<View16 v-else />
						</template>
					</TooltipButton>
					<CopyToClipboardButton
						:value-to-copy="passedKey"
						value-name="Key ID"
					/>
				</div>
				<AlertMessage type="warning">
					This key is a <b>secret</b>. We will not be able to show you the value again after you close this
					dialog.
				</AlertMessage>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="closeDialog"
				>
					Close
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import View16 from "@carbon/icons-vue/es/view/16";
import ViewOff16 from "@carbon/icons-vue/es/view--off/16";

// Components
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import AlertMessage from "@/components/Common/AlertMessage.vue";

// Stores
import { useUserStore } from "@/stores/user";

export default defineComponent({
	name: "APIKeyCreationDialog",
	components: {
		Close16,
		View16,
		ViewOff16,
		CopyToClipboardButton,
		TooltipButton,
		AlertMessage,
	},
	emits: ["update:showDialog"],
	data: () => ({
		buttonIsLoading: false,
		email: "",
		showSupportMessage: false,
		keyVisible: false,
	}),
	props: {
		showDialog: { type: Boolean, default: false },
		passedName: {
			type: String,
			default: null,
		},
		passedKey: {
			type: String,
			default: null,
		},
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
	},
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	methods: {
		toggleKeyVisible() {
			this.keyVisible = !this.keyVisible;
		},
		closeDialog() {
			this.$emit("update:showDialog", false);
		},
	},
});
</script>

<style>
.password-field .v-text-field__details {
	padding-left: 0;
	padding-right: 0;
}

.obscured input {
	filter: blur(var(--sgnl-space-8));
}
</style>
